/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useState, useEffect } from "react";
import { Box } from "@mui/material";
import {
  CustomDivider,
  PanelContentsArea,
} from "./Util";
import { TaskRegisterPanelFooter } from "components/Common/TaskRegisterPanelFooter";
import { useUserContext } from "context/UserContext";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import { DisplayItem } from "components/Common/DisplayItem";
import { Const } from "const";
import { ResultJobList } from "client";
import { useAwvDataContext } from "context/AwvDataProvider";
import { DisplayItemWithTimeIcon } from "./DisplayItemWithTimeIcon";
import moment from "moment-timezone";
import { getNodeType } from "components/Map/NodeMapOverlay";

/**
 * Check can edit.
 * @param {ResultTaskList} task
 * @param {string|undefined} roleName
 * @return {boolean} can it
 */
function canEdit(task: ResultJobList, roleName?: string): boolean {
  return (
    roleName !== "awv_operator" &&
    task.jobSetStatus === 3 &&
    task.taskStatus === 1
  );
}

interface MultipleDetailPanelProps {
  task: ResultJobList;
  jobs: ResultJobList[];
  onClose: (isEdited: boolean) => void;
}

export const MultipleDetailPanel: FC<MultipleDetailPanelProps> = ({
  task,
  jobs,
  onClose,
}) => {
  const { t } = useTranslation();
  const { roleName } = useUserContext();
  const { awvData } = useAwvDataContext();
  const [nameId, setNameId] = useState<string | null>();

  useEffect(() => {
    setNameId(
      awvData?.awvInfos?.find(
        (awv) => awv.frameTypeAndNumber === task.frameTypeAndNumber
      )?.nameId
    );
  }, [awvData, task.frameTypeAndNumber]);
  return (
    <Container>
      <HeaderArea>
        {task.frameTypeAndNumber}
      </HeaderArea>
      <PanelContentsArea>
        {jobs.map((job, index) => (
          <CustomBox>
            <DisplayItem
              title={t("AWVUI-MSG196")}
              // value={job.nodeCategory.toString()}
              value={
                job.nodeCategory === 1 ? t("AWVUI-MSG20") :
                job.nodeCategory === 2 ? t("AWVUI-MSG192") :
                job.nodeCategory === 3 ? t("AWVUI-MSG193") :
                job.nodeCategory === 4 ? t("AWVUI-MSG194") :
                job.nodeCategory === 5 ? t("AWVUI-MSG200") :
                job.nodeCategory === 6 ? t("AWVUI-MSG195") :
                "--"
              }
            />
            <CustomDivider />
            <DisplayItem
              title={t("AWVUI-MSG197")}
              value={getNodeType(job.nodeName)["label"]}
            />
            <CustomDivider />
            <DisplayItem
              title={t("AWVUI-MSG35")}
              value={`${t("AWVUI-MSG77")}-${nameId ?? "--"}`}
            />
            <CustomDivider />
            <DisplayItemWithTimeIcon
              isAutoStart={!!task.extensionTimeLoad}
              title={t("AWVUI-MSG198")}
              value={moment(job.planningArrivalDatetime!.toString()).format('YYYY/MM/DD HH:mm:ss')}
            />
            <CustomDivider />
            <DisplayItemWithTimeIcon
              isAutoStart={!!task.extensionTimeLoad}
              title={t("AWVUI-MSG199")}
              value={moment(job.planningDepartureDatetime!.toString()).format('YYYY/MM/DD HH:mm:ss')}
            />
            <CustomDivider />
            {job.nodeCategory !== 6 && (
              <>
                <DisplayItem
                  title={t("AWVUI-MSG188")}
                  value={job.materialName !== "" ? job.materialName! : "--"}
                />
                <CustomDivider />
                <DisplayItem
                  title={t("AWVUI-MSG190")}
                  value={job.loadingQuantity ? job.loadingQuantity.toString() : "--"}
                />
              </>
            )}
            {job.nodeCategory === 6 && (
              <>
              <DisplayItem
                title={t("AWVUI-MSG201")}
                value={job.materialName !== "" ? job.materialName! : "--"}
              />
            </>
            )}
          </CustomBox>
        )
      )}
      </PanelContentsArea>
      <TaskRegisterPanelFooter
        leftLabel={t("AWVUI-MSG22")}
        leftHandle={() => onClose(false)}
        disabled={!canEdit(task, roleName)}
      />
    </Container>
  );
};

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
}));

const CustomBox = styled(Box)(({ theme }) => ({
  width: "100%",
  borderBottom: "1px solid black",
  padding: "0.5rem 2rem",
}));

const HeaderArea = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(1, 4),
  width: "100%",
  flexDirection: "column",
  backgroundColor: Const.sidePanelHeaderColor,
  fontWeight: "bold",
  fontSize: "1.2rem",
}));
